import { AsyncPipe } from '@angular/common';
import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import {
  DaterangeConfig,
  PageToolbarComponent,
  DaterangePickerComponent,
} from '@next/core-lib/components';
import { SiteActions, SiteSelectors, SiteState } from '@next/core-lib/site';
import { TableColumn, TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import * as FeedActions from '../../store/actions/feed.actions';
import { getDeliveryLines } from './../../store/selectors/feed.selectors';

@Component({
  selector: 'app-deliveries',
  templateUrl: './deliveries.component.html',
  styleUrls: ['./deliveries.component.scss'],
  imports: [PageToolbarComponent, DaterangePickerComponent, TableComponent, AsyncPipe],
})
export class DeliveriesComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);

  public pageTitle$ = this.store.pipe(select(SiteSelectors.selectPageTitle));
  public customDateRangeConfig: { presets: Presets[]; initial: DateRange };
  public tableConfig: TableConfig;
  private readonly destroy$ = new Subject<void>();

  private getDeliveryLines$ = this.store.select(getDeliveryLines);

  ngOnInit(): void {
    this.store.dispatch(SiteActions.updatePageTitle({ pageTitle: 'feed.deliveries.title' }));

    const presets = new DaterangeConfig().defaultConfig.presets;
    this.customDateRangeConfig = {
      presets: [...presets],
      initial: presets[0],
    };

    this.tableConfig = {
      selectColumn: false,
      alternateRows: true,
      pagination: false,
      footer: false,
      dataSource: new MatTableDataSource(),
      columns: [
        this.getColumn('deliveryDate', TableColumnTypes.DATE),
        this.stringColumn('externalProductId'),
        this.stringColumn('productTitle'),
        this.numberColumn('unitQuantity', '1.0-0'),
        this.numberColumn('nettoAmount', '1.2-2'),
      ],
    };

    this.getDeliveryLines$
      .pipe(
        filter((deliveryLines) => !!deliveryLines),
        takeUntil(this.destroy$),
      )
      .subscribe((milkDeliverySummary) => {
        this.tableConfig.dataSource.data = milkDeliverySummary;
      });

    this.store.dispatch(
      FeedActions.loadDeliveryLines({
        dateFrom: this.customDateRangeConfig.presets[0].startDate.toISOString(),
        dateTo: this.customDateRangeConfig.presets[0].endDate.toISOString(),
      }),
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onDateRangeChanged(dateRange: DateRange): void {
    this.store.dispatch(
      FeedActions.loadDeliveryLines({
        dateFrom: dateRange.startDate.toISOString(),
        dateTo: dateRange.endDate.toISOString(),
      }),
    );
  }

  private numberColumn(id: string, digitsInfo: string): TableColumn {
    return this.getColumn(id, TableColumnTypes.NUMBER, digitsInfo);
  }

  private stringColumn(id: string): TableColumn {
    return this.getColumn(id, TableColumnTypes.STRING);
  }

  private getColumn(
    id: string,
    type: TableColumnTypes = TableColumnTypes.STRING,
    digitsInfo?: string,
  ): TableColumn {
    return {
      id,
      label: `feed.deliveries.table.${id}`,
      type,
      template: null,
      digitsInfo: type === TableColumnTypes.NUMBER ? digitsInfo : undefined,
      disableSort: true,
      headerClassName: 'flex__child--1 mat-cell-nospacing no-word-wrap',
      className: 'flex__child--1 mat-cell-nospacing',
      footerClassName: 'flex__child--1 mat-cell-nospacing footer',
    };
  }
}

interface DateRange {
  description?: string;
  startDate: Date;
  endDate: Date;
}

interface Presets {
  description: string;
  startDate: Date;
  endDate: Date;
}
